import { toast } from "sonner";
import { OriginalStyle } from './types/style-types';

/**
 * Export the OriginalStyle type for use in other files
 */
export type { OriginalStyle };

// Keep a reference to the temporary style element to ensure we remove the correct one
let tempStyleElement: HTMLStyleElement | null = null;

/**
 * Saves the original state of an element
 * Useful for tracking changes that need to be restored later
 */
const saveOriginalState = (
  element: Element, 
  properties: Record<string, string>,
  originalStyles: OriginalStyle[]
): void => {
  originalStyles.push({
    element,
    styles: { ...properties }
  });
};

/**
 * Ensures an element has an ID, creating one if needed
 */
const ensureElementId = (element: HTMLElement): string => {
  if (!element.id) {
    element.id = `export-container-${Date.now()}`;
  }
  return element.id;
};

/**
 * Enforces light theme for consistent exports
 */
const enforceLightTheme = (
  containerRef: HTMLDivElement,
  originalStyles: OriginalStyle[]
): void => {
  // Store original theme class for HTML element
  const htmlElement = document.documentElement;
  const htmlHasLightClass = htmlElement.classList.contains('light');
  const htmlHasDarkClass = htmlElement.classList.contains('dark');
  
  if (!htmlHasLightClass || htmlHasDarkClass) {
    saveOriginalState(htmlElement, { classList: htmlElement.className }, originalStyles);
    
    // Force light theme on document
    if (htmlHasDarkClass) {
      htmlElement.classList.remove('dark');
    }
    if (!htmlHasLightClass) {
      htmlElement.classList.add('light');
    }
  }
  
  // Store original theme class for container
  const containerHasLightClass = containerRef.classList.contains('light');
  const containerHasDarkClass = containerRef.classList.contains('dark');
  
  if (!containerHasLightClass || containerHasDarkClass) {
    saveOriginalState(containerRef, { className: containerRef.className }, originalStyles);
    
    // Force light theme on container
    if (containerHasDarkClass) {
      containerRef.classList.remove('dark');
    }
    if (!containerHasLightClass) {
      containerRef.classList.add('light');
    }
  }
  
  // Store and update CSS variables if needed
  const currentQuoteBubbleColor = document.documentElement.style.getPropertyValue('--quote-bubble-color');
  if (currentQuoteBubbleColor !== '#f3f4f6') {
    saveOriginalState(document.documentElement, { '--quote-bubble-color': currentQuoteBubbleColor }, originalStyles);
    document.documentElement.style.setProperty('--quote-bubble-color', '#f3f4f6');
  }
};

/**
 * Preserves the current visual state for export
 * Takes a minimal approach to style modification while enforcing light theme
 */
export const enhanceElementsForExport = (
  containerRef: HTMLDivElement
): OriginalStyle[] => {
  const originalStyles: OriginalStyle[] = [];

  try {
    // Clean up any previous temporary style if it exists
    if (tempStyleElement && tempStyleElement.parentNode) {
      tempStyleElement.parentNode.removeChild(tempStyleElement);
      tempStyleElement = null;
    }
    
    // Ensure container has an ID for our CSS selectors
    const containerId = ensureElementId(containerRef);
    
    // Always enforce light theme first (important for consistent exports)
    enforceLightTheme(containerRef, originalStyles);
    
    // === CAPTURE CONTAINER PROPERTIES ===
    // Save the original overflow properties and transform
    const containerStyle = window.getComputedStyle(containerRef);
    saveOriginalState(containerRef, {
      'overflow': containerStyle.overflow,
      'overflow-x': containerStyle.overflowX,
      'overflow-y': containerStyle.overflowY,
      'transform': containerRef.style.transform || ''
    }, originalStyles);
    
    // === ADD EXPORT-SPECIFIC STYLES ===
    // Create a targeted style sheet for export that preserves layout
    tempStyleElement = document.createElement('style');
    tempStyleElement.id = 'journey-map-export-styles';
    tempStyleElement.textContent = `
      /* Force light theme */
      html.dark, body.dark, .dark {
        color-scheme: light !important;
        background-color: #ffffff !important;
        color: #000000 !important;
      }
      
      /* Main container styles */
      #${containerId} {
        overflow: visible !important;
        transform: scale(1) !important;
        background-color: #ffffff !important;
      }
      
      /* Ensure all elements maintain their current size */
      #${containerId} * {
        page-break-inside: avoid;
        break-inside: avoid;
      }
      
      /* Ensure no scrollbars appear in the exported document */
      #${containerId} .overflow-x-auto,
      #${containerId} .overflow-y-auto,
      #${containerId} .overflow-auto {
        overflow: visible !important;
      }
      
      /* Fix flex wrapping issues and enforce landscape-friendly layout */
      #${containerId} .flex.items-start.gap-4.overflow-x-auto { 
        flex-wrap: nowrap !important;
        width: max-content !important;
        display: flex !important;
        flex-direction: row !important;
      }
      
      /* Ensure stages are properly aligned in a row */
      #${containerId} .stage-card {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        margin-right: 1rem !important;
      }
      
      /* Hide interactive elements */
      #${containerId} button.glassmorphism.fixed,
      #${containerId} .fixed.bottom-6 {
        display: none !important;
      }
      
      /* Text readability enhancements */
      #${containerId} p, #${containerId} h1, #${containerId} h2, #${containerId} h3, 
      #${containerId} span, #${containerId} div {
        color: #000000 !important;
        text-shadow: none !important;
      }
      
      /* Card background enhancements */
      #${containerId} .bg-card {
        background-color: #ffffff !important;
        box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24) !important;
      }
      
      /* Fix profile photo aspect ratio preservation */
      #${containerId} .h-24.w-24.rounded-full,
      #${containerId} .h-28.w-28.rounded-full,
      #${containerId} .rounded-full {
        overflow: hidden !important;
        position: relative !important;
        aspect-ratio: 1/1 !important;
      }
      
      /* Fix avatar images to maintain proper aspect ratio */
      #${containerId} .avatar-image,
      #${containerId} .h-24.w-24.rounded-full img, 
      #${containerId} .h-28.w-28.rounded-full img,
      #${containerId} .rounded-full img,
      #${containerId} .avatar img {
        object-fit: cover !important;
        width: 100% !important;
        height: 100% !important;
        position: absolute !important;
        top: 0 !important;
        left: 0 !important;
        aspect-ratio: 1/1 !important;
      }
    `;
    document.head.appendChild(tempStyleElement);
    
    // === ENSURE VISIBILITY OF KEY ELEMENTS ===
    // Find all containers that need to be fully visible
    const stageContainers = containerRef.querySelectorAll('.stage-card');
    stageContainers.forEach(container => {
      if (container instanceof HTMLElement) {
        // Save original style
        saveOriginalState(container, {
          'display': container.style.display,
          'visibility': container.style.visibility,
          'opacity': container.style.opacity
        }, originalStyles);
        
        // Ensure stage is visible
        container.style.display = 'block';
        container.style.visibility = 'visible';
        container.style.opacity = '1';
      }
    });
    
    // Enforce a landscape-friendly layout for the stages container
    const stagesContainer = containerRef.querySelector('.flex.items-start.gap-4.overflow-x-auto');
    if (stagesContainer instanceof HTMLElement) {
      saveOriginalState(stagesContainer, {
        'display': stagesContainer.style.display,
        'flex-wrap': stagesContainer.style.flexWrap,
        'flex-direction': stagesContainer.style.flexDirection,
        'width': stagesContainer.style.width
      }, originalStyles);
      
      stagesContainer.style.display = 'flex';
      stagesContainer.style.flexWrap = 'nowrap';
      stagesContainer.style.flexDirection = 'row';
      stagesContainer.style.width = 'max-content';
    }
    
    // === ENSURE PROPER AVATAR RENDERING ===
    // Find all profile photos and ensure they have proper aspect ratio
    const avatarImages = containerRef.querySelectorAll('.avatar-image, .rounded-full img');
    avatarImages.forEach(image => {
      if (image instanceof HTMLElement) {
        // Save original style
        saveOriginalState(image, {
          'objectFit': image.style.objectFit,
          'width': image.style.width,
          'height': image.style.height
        }, originalStyles);
        
        // Apply proper object-fit to preserve aspect ratio
        image.style.objectFit = 'cover';
        image.style.width = '100%';
        image.style.height = '100%';
      }
    });
    
    // Fix avatar containers as well
    const avatarContainers = containerRef.querySelectorAll('.rounded-full');
    avatarContainers.forEach(container => {
      if (container instanceof HTMLElement) {
        // Save original style
        saveOriginalState(container, {
          'overflow': container.style.overflow,
          'position': container.style.position
        }, originalStyles);
        
        // Make sure the container properly crops the content
        container.style.overflow = 'hidden';
        container.style.position = 'relative';
      }
    });
    
    return originalStyles;
  } catch (error) {
    console.error("Error enhancing elements for export:", error);
    // Try to clean up temp element if there was an error
    if (tempStyleElement && tempStyleElement.parentNode) {
      tempStyleElement.parentNode.removeChild(tempStyleElement);
      tempStyleElement = null;
    }
    return originalStyles;
  }
};

/**
 * Restores the original styles of elements
 */
export const restoreOriginalStyles = (originalStyles: OriginalStyle[]): void => {
  try {
    // Restore original element styles in reverse order (to handle parent-child relationships)
    for (let i = originalStyles.length - 1; i >= 0; i--) {
      const {element, styles} = originalStyles[i];
      
      // Skip elements that no longer exist in the DOM
      if (!element || !document.body.contains(element as Node)) {
        continue;
      }
      
      Object.entries(styles).forEach(([prop, value]) => {
        try {
          if (prop === 'classList' || prop === 'className') {
            (element as HTMLElement).className = value as string;
          } else if (value === undefined || value === null || value === '') {
            // If value was undefined/null/empty, remove the property
            (element as HTMLElement).style.removeProperty(prop);
          } else {
            (element as HTMLElement).style[prop as any] = value;
          }
        } catch (styleError) {
          console.error(`Error restoring style property ${prop}:`, styleError);
        }
      });
    }
  } catch (restoreError) {
    console.error("Error restoring original styles:", restoreError);
  } finally {
    // Always try to remove our temporary style element
    if (tempStyleElement && tempStyleElement.parentNode) {
      tempStyleElement.parentNode.removeChild(tempStyleElement);
      tempStyleElement = null;
    }
  }
};
